import React from "react";
import {
 Card,
 H1,
 H3,
 Text,
 Button,
 HTMLSelect,
 Tag,
 Spinner,
 NonIdealState,
 Icon,
 Switch,
} from "@blueprintjs/core";
import SingleSelectWrapper from "../../../micro/utils/SingleSelectWrapper";
import { DateInput } from "@blueprintjs/datetime";
import { inject, observer } from "mobx-react";
import { apiCallPost } from "../../../micro/fns/util";
import cogoToast from "cogo-toast";
import moment from "moment";
import { withRouter } from "react-router-dom";

class IPDAgeGender extends React.Component {
 state = {
  // Data
  appointments: [],
  allAppointments: [], // All appointments data for client-side filtering
  branches: [],
  doctors: [],

  // Loading
  isLoading: false,
  isGenerating: false,

  // Filters
  reportDate: new Date(),
  selectedBranch: "",
  selectedDoctor: "",
  selectedBranchName: null,
  selectedDoctorName: null,
  selectedStatus: "", // All statuses by default

  // UI
  showDetailed: false,
 };

 componentDidMount = async () => {
  await this.loadInitialData();
 };

 // Navigate to surgery details page
 navigateToDetails = (surgery) => {
  const detailsPath = `/v3/workflow/surgery-booking/${
   surgery._id || surgery.docNo || surgery.bookingReference || surgery.surgeryId
  }`;
  window.open(detailsPath, "_blank", "noopener,noreferrer");
 };

 componentDidUpdate(prevProps) {
  if (
   prevProps.authStore?.selectedBranchName !==
   this.props.authStore?.selectedBranchName
  ) {
   const preferredName = this.props.authStore?.selectedBranchName;
   if (!preferredName) {
    this.setState({
     selectedBranch: "",
     selectedBranchName: null,
    });
    return;
   }
   const match = (this.state.branches || []).find(
    (b) => (b.name || "").toLowerCase() === String(preferredName).toLowerCase()
   );
   if (match && match._id !== this.state.selectedBranch) {
    this.setState(
     {
      selectedBranch: match._id,
      selectedBranchName: match.name,
     },
     () => {
      // Auto-regenerate report when global branch selection changes
      this.generateReport();
     }
    );
   }
  }
 }

 loadInitialData = async () => {
  try {
   this.setState({ isLoading: true });
   const branches = await this.loadBranches();
   await this.loadDoctors();
   // Default branch from global selection if available
   if (Array.isArray(branches) && branches.length) {
    const preferredName = this.props.authStore?.selectedBranchName;
    const norm = (s) =>
     String(s || "")
      .trim()
      .toLowerCase();
    const match = preferredName
     ? branches.find((b) => norm(b.name) === norm(preferredName))
     : null;
    if (match)
     this.setState({
      selectedBranch: match._id,
      selectedBranchName: match.name,
     });
   }
   await this.generateReport();
  } catch (err) {
   console.error("Error loading initial data:", err);
   this.loadDummyData();
  } finally {
   this.setState({ isLoading: false });
  }
 };

 loadBranches = async () => {
  try {
   // Use the sites API and normalize like BookingRegister.js
   const response = await apiCallPost(
    `/rai/${this.props.authStore.prefix}/getAllSites`,
    {}
   );

   const rawBranches = response?.data || response || [];
   const branches = (Array.isArray(rawBranches) ? rawBranches : [])
    .map((site, idx) => ({
     _id:
      site?._id ||
      site?.id ||
      site?.siteId ||
      site?.value ||
      site?.key ||
      (typeof site === "string" ? site : String(idx)),
     name:
      site?.site ||
      site?.siteName ||
      site?.name ||
      site?.branchName ||
      site?.label ||
      (typeof site === "string" ? site : `Site ${idx + 1}`),
    }))
    .sort((a, b) => (a.name || "").localeCompare(b.name || ""));

   console.log("🔍 DEBUG: Branches loaded:", branches.length);
   this.setState({ branches });
   return branches;
  } catch (err) {
   console.error("Error loading branches:", err);
   // Try alternative API endpoint
   try {
    const response = await apiCallPost(
     `/meta-sx/${this.props.authStore.prefix}/branchMaster/getAll`,
     {}
    );
    const rawBranches = response?.data || response || [];
    const branches = (Array.isArray(rawBranches) ? rawBranches : [])
     .map((site, idx) => ({
      _id: site?._id || site?.id || String(idx),
      name:
       site?.site ||
       site?.siteName ||
       site?.name ||
       site?.branchName ||
       site?.label ||
       `Site ${idx + 1}`,
     }))
     .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    this.setState({ branches });
    return branches;
   } catch (fallbackErr) {
    console.error("Fallback branch API also failed:", fallbackErr);
    // Return dummy branches if both APIs fail
    const dummyBranches = [
     { _id: "branch1", name: "Barrackpore" },
     { _id: "branch2", name: "Kolkata" },
     { _id: "branch3", name: "Howrah" },
     { _id: "branch4", name: "Dum Dum" },
    ];
    this.setState({ branches: dummyBranches });
    return dummyBranches;
   }
  }
 };

 loadDoctors = async () => {
  try {
   // Use the existing doctor/consultant API
   const response = await apiCallPost(
    `/rai/${this.props.authStore.prefix}/getAllConsultants`,
    {}
   );

   // Transform consultants to doctor format
   const doctors = Array.isArray(response)
    ? response.map((consultant) => ({
       _id: consultant._id || consultant.id,
       name: consultant.name || consultant.consultantName,
       specialization: consultant.specialization || consultant.department,
       phone: consultant.phone || consultant.contactNumber,
       email: consultant.email || consultant.emailId,
       shortcode: consultant.shortcode || consultant.code,
      }))
    : [];

   console.log("🔍 DEBUG: Doctors/Consultants loaded:", doctors.length);
   this.setState({ doctors });
   return doctors;
  } catch (err) {
   console.error("Error loading doctors:", err);
   // Try alternative API endpoint
   try {
    const response = await apiCallPost(
     `/meta-sx/${this.props.authStore.prefix}/doctorMaster/getAll`,
     {}
    );
    const doctors = response?.data || response || [];
    this.setState({ doctors });
    return doctors;
   } catch (fallbackErr) {
    console.error("Fallback doctor API also failed:", fallbackErr);
    // Return dummy doctors if both APIs fail
    const dummyDoctors = [
     { _id: "doc1", name: "DR. ARIJIT MITRA" },
     { _id: "doc2", name: "DR. ARNAB DAS" },
     { _id: "doc3", name: "DR. SUDIPTA ROY" },
     { _id: "doc4", name: "DR. PRITAM SARKAR" },
    ];
    this.setState({ doctors: dummyDoctors });
    return dummyDoctors;
   }
  }
 };

 generateReport = async () => {
  try {
   this.setState({ isGenerating: true });

   const payload = {
    page: 1,
    sortBy: "scheduledDate",
    sortOrder: "asc",
    startDate: moment(this.state.reportDate).format("YYYY-MM-DD"),
    endDate: moment(this.state.reportDate).format("YYYY-MM-DD"),
    dateField: "scheduledDate", // Explicitly filter by scheduledDate, not originalScheduledDate
   };

   // Add filters if selected
   if (this.state.selectedBranch) {
    // Add both branchId and branchName to maximize API compatibility
    const selectedBranchObj = this.state.branches.find(
     (b) => b._id === this.state.selectedBranch
    );
    payload.branchId = this.state.selectedBranch;
    if (selectedBranchObj && selectedBranchObj.name) {
     payload.branchName = selectedBranchObj.name;
    }
    console.log(
     "🔍 DEBUG: Branch filter applied - ID:",
     this.state.selectedBranch,
     "Name:",
     selectedBranchObj?.name
    );
   }

   // Note: Doctor filtering is now handled client-side for better UX
   // Only apply server-side filtering for date and branch
   console.log("🔍 DEBUG: API filters applied:", {
    dateRange: payload.startDate,
    branchFilter: this.state.selectedBranch || "All branches",
    doctorFilterHandledClientSide: true,
   });

   // Use the existing getSurgeries API
   console.log(
    "🔍 DEBUG: IPD Age/Gender API Payload:",
    JSON.stringify(payload, null, 2)
   );
   const response = await apiCallPost(
    `/v3/${this.props.authStore.prefix}/neoSxBookingMaster/getSurgeries`,
    payload
   );

   // Handle the API response structure properly
   const surgeries = response?.data?.items
    ? response.data.items
    : response?.data || response || [];

   console.log("🔍 DEBUG: IPD Age/Gender API Response:", response);
   console.log("🔍 DEBUG: Surgeries loaded for report:", surgeries.length);

   // Filter out surgeries marked as postponed to avoid showing them in the report
   const filteredSurgeries = (surgeries || []).filter((surgery) => {
    const normalizedStatus = String(
     surgery.status || surgery.surgeryStatus || surgery.bookingStatus || ""
    )
     .trim()
     .toLowerCase();
    return normalizedStatus !== "postponed";
   });

   if (filteredSurgeries.length !== surgeries.length) {
    console.log(
     "ℹ️ INFO: Skipping postponed surgeries",
     surgeries.length - filteredSurgeries.length
    );
   }

   // Debug: Show branch info in returned surgeries
   if (filteredSurgeries.length > 0) {
    console.log("🔍 DEBUG: Sample surgery branch data:", {
     branchId: filteredSurgeries[0]?.branchId,
     branchName: filteredSurgeries[0]?.branchName,
     branch: filteredSurgeries[0]?.branch,
    });
   }

   // Transform the surgery data to match appointment register format
   const transformedAppointments =
    this.transformSurgeriesToAppointments(filteredSurgeries);

   // Store all appointments for client-side filtering
   this.setState(
    {
     allAppointments: transformedAppointments,
    },
    () => {
     // Apply client-side filters after storing all data
     this.applyClientSideFilters();
    }
   );

   if (!transformedAppointments || !transformedAppointments.length) {
    cogoToast.info("No data found for selected date/branch");
   }
  } catch (err) {
   console.error("Error generating IPD Age/Gender report:", err);
   cogoToast.error("Failed to generate report. Showing sample data");
   this.loadDummyData();
  } finally {
   this.setState({ isGenerating: false });
  }
 };

 // Helper function to calculate age from date of birth
 calculateAge = (dob) => {
  if (!dob) return null;
  try {
   const birthDate = new Date(dob);
   const today = new Date();
   let age = today.getFullYear() - birthDate.getFullYear();
   const monthDiff = today.getMonth() - birthDate.getMonth();

   if (
    monthDiff < 0 ||
    (monthDiff === 0 && today.getDate() < birthDate.getDate())
   ) {
    age--;
   }

   return age;
  } catch (err) {
   console.error("Error calculating age:", err);
   return null;
  }
 };

 // Normalize gender strings to handle values like M/F/Male/Female
 normalizeGender = (gender) => {
  const normalized = String(gender || "")
   .trim()
   .toLowerCase();
  if (!normalized) return null;
  if (normalized.startsWith("m")) return "male";
  if (normalized.startsWith("f")) return "female";
  return null;
 };

 // Helper function to determine laterality from selectedSurgeries
 determineLaterality = (selectedSurgeries) => {
  if (
   !selectedSurgeries ||
   !Array.isArray(selectedSurgeries) ||
   selectedSurgeries.length === 0
  ) {
   return "RE"; // Default to right eye
  }

  // Get all unique lateralities
  const lateralities = selectedSurgeries
   .map((surgery) => surgery.laterality)
   .filter((lat) => lat && lat.trim())
   .map((lat) => lat.trim().toUpperCase());

  // Remove duplicates
  const uniqueLateralities = [...new Set(lateralities)];

  // If no lateralities found, default to RE
  if (uniqueLateralities.length === 0) {
   return "RE";
  }

  // If only one unique laterality, return it
  if (uniqueLateralities.length === 1) {
   return uniqueLateralities[0];
  }

  // If multiple different lateralities, return BE (Both Eyes)
  return "BE";
 };

 // Helper function to format selected surgeries names
 formatSelectedSurgeries = (selectedSurgeries) => {
  if (
   !selectedSurgeries ||
   !Array.isArray(selectedSurgeries) ||
   selectedSurgeries.length === 0
  ) {
   return "Surgery"; // Default fallback
  }

  return selectedSurgeries
   .map((surgery) => {
    return surgery.name || surgery.surgeryName || "Unknown Surgery";
   })
   .filter((name) => name && name.trim())
   .join("\n"); // Join with newline for multiple surgeries
 };

 // Transform surgery booking data to appointment register format
 transformSurgeriesToAppointments = (surgeries) => {
  return surgeries.map((surgery, index) => ({
   _id: surgery._id || surgery.id || `surgery_${index}`,
   docNo:
    surgery.bookingReference ||
    surgery._id?.toString() ||
    `BKP25U${String(index).padStart(5, "0")}`,
   surgeryId:
    surgery._id || surgery.id || `SX${String(index + 1).padStart(3, "0")}`,
   patientId:
    surgery.patientGenericPid ||
    surgery.patientId ||
    surgery.patientOriginalData?.sysPid ||
    `P${String(index).padStart(10, "0")}`,
   patientName:
    surgery.patientName ||
    surgery.patientOriginalData?.name ||
    "Unknown Patient",
   appointmentDate: surgery.scheduledDate
    ? new Date(surgery.scheduledDate)
    : new Date(),
   scheduledTime: surgery.scheduledTime || "09:00",
   patientAge:
    surgery.patientAge ||
    this.calculateAge(surgery.patientDob) ||
    this.calculateAge(surgery.patientOriginalData?.dob),
   patientGender:
    surgery.patientSex || surgery.patientOriginalData?.sex || "Unknown",
   patientPhone:
    surgery.patientPhone || surgery.patientOriginalData?.phone || "",
   patientEmail:
    surgery.patientEmail || surgery.patientOriginalData?.email || "",
   patientAddress:
    surgery.patientAddress || surgery.patientOriginalData?.address || "",
   eye: this.determineLaterality(surgery.selectedSurgeries),
   diet: surgery.isDiabetic ? "DIABETIC" : "NON-DIABETIC",
   lens: surgery.iolType || surgery.implantType || "NO LENS",
   specialNote: surgery.specialInstructions || surgery.preOpInstructions || "",
   surgeryName:
    this.formatSelectedSurgeries(surgery.selectedSurgeries) ||
    surgery.procedure ||
    surgery.surgeryType ||
    surgery.type ||
    "Surgery",
   // Store the original selectedSurgeries for reference
   selectedSurgeries: surgery.selectedSurgeries,
   remarks: surgery.additionalNotes || surgery.notes || "",
   patientPanel:
    surgery.payorPanel || surgery.patientOriginalData?.panel || "Cash",
   doctorName: surgery.doctorName || "Unknown Doctor",
   doctorId: surgery.doctorId || surgery.doctor?._id || "",
   branchName: surgery.branchName || "Unknown Branch",
   branchId: surgery.branchId || surgery.branch?._id || "",
   createdByName: surgery.createdByName || surgery.createdBy || "System",
   createdAt: surgery.createdAt || new Date(),
   bookedAt: surgery.createdAt || new Date(),
   status: surgery.status || "confirmed",
   urgencyLevel: surgery.urgencyLevel || "routine",
   anaesthesiaType:
    surgery.anaesthesiaType || surgery.anesthesiaType || "topical",
   estimatedDuration: surgery.estimatedDuration || 60,
   // Additional fields that might be useful
   whatsappNumber: surgery.whatsappNumber || "",
   enableWhatsappConfirmation: surgery.enableWhatsappConfirmation || false,
   enableEmailConfirmation: surgery.enableEmailConfirmation || false,
   timeFrame: surgery.timeFrame || "morning",
   slotCapacity: surgery.slotCapacity || 1,
   slotBookedCount: surgery.slotBookedCount || 1,
  }));
 };

 loadDummyData = () => {
  const today = moment(this.state.reportDate).startOf("day");
  const sample = [
   {
    _id: "apt1",
    docNo: "BKP25U00012",
    surgeryId: "SX001",
    patientId: "P0401405502",
    patientName: "RAGHU NATH DAS",
    appointmentDate: today.clone().hour(9).minute(0).toDate(),
    patientAge: 50,
    patientGender: "Male",
    eye: "RE",
    diet: "NON-DIABETIC",
    lens: "NO LENS",
    specialNote: "",
    surgeryName: "TA INTRAVITREAL RAZUMAB( RANIBIZUMAB)",
    remarks: "PAYING BED HTN",
    doctorName: "DR. ARNAB DAS",
   },
   {
    _id: "apt2",
    docNo: "BKP25U00013",
    surgeryId: "SX002",
    patientId: "P0401405503",
    patientName: "SUNITA DEVI",
    appointmentDate: today.clone().hour(10).minute(30).toDate(),
    patientAge: 65,
    patientGender: "Female",
    eye: "LE",
    diet: "DIABETIC",
    lens: "NO LENS",
    specialNote: "",
    surgeryName: "LA VITRECTOMY (L1)+MIVS+SOI",
    remarks: "PAYING BED",
    doctorName: "DR. ARNAB DAS",
   },
   {
    _id: "apt3",
    docNo: "BKP25U00014",
    surgeryId: "SX003",
    patientId: "P0401405504",
    patientName: "AMIT KUMAR SINGH",
    appointmentDate: today.clone().hour(11).minute(15).toDate(),
    patientAge: 45,
    patientGender: "Male",
    eye: "RE",
    diet: "NON-DIABETIC",
    lens: "NO LENS",
    specialNote: "",
    surgeryName:
     "TA PHACO WITH HYDROPHOBIC J&J ASPHERIC TECNIS 1/ HOYA VIVINEX MULTISERT IOL+VISCOAT+TOPICAL",
    remarks: "PAYING BED",
    doctorName: "DR. ARNAB DAS",
   },
   {
    _id: "apt4",
    docNo: "BKP25U00015",
    surgeryId: "SX004",
    patientId: "P0401405505",
    patientName: "PRIYA SHARMA",
    appointmentDate: today.clone().hour(14).minute(0).toDate(),
    patientAge: 18,
    patientGender: "Female",
    eye: "LE",
    diet: "NON-DIABETIC",
    lens: "22.00 T1 TECNIS-1",
    specialNote: "",
    surgeryName:
     "TA PHACO WITH HYDROPHOBIC J&J ASPHERIC TECNIS 1/ HOYA VIVINEX MULTISERT IOL+VISCOAT+TOPICAL",
    remarks: "PAYING BED",
    doctorName: "DR. ARNAB DAS",
   },
  ];
  this.setState(
   {
    allAppointments: sample,
   },
   () => {
    // Apply client-side filters after storing dummy data
    this.applyClientSideFilters();
   }
  );
 };

 handleDateChange = (date) => {
  this.setState({ reportDate: date });
 };

 handleFilterChange = (field, value) => {
  if (field === "selectedBranch") {
   this.setState({ selectedBranch: value, selectedDoctor: "" }, () => {
    // Auto-regenerate report when branch changes
    this.generateReport();
   });
   return;
  }
  this.setState({ [field]: value });
 };

 // Helper function to create branch items for dropdown
 createBranchSelectItems = () => {
  return this.state.branches.map((branch) => branch.name).filter(Boolean);
 };

 // Helper function to create doctor items for dropdown with shortcode formatting
 createDoctorSelectItems = () => {
  return this.state.doctors
   .map((doctor) => {
    const shortcode = doctor.shortcode ? `${doctor.shortcode} | ` : "";
    const name = doctor.name;
    return `${shortcode}${name}`;
   })
   .filter(Boolean);
 };

 // Helper function to get formatted doctor name
 getFormattedDoctorName = (doctor) => {
  if (!doctor) return null;
  const shortcode = doctor.shortcode ? `${doctor.shortcode} | ` : "";
  const name = doctor.name;
  return `${shortcode}${name}`;
 };

 // Helper function to extract doctor name from formatted string
 extractDoctorNameFromFormatted = (formattedName) => {
  if (!formattedName) return null;
  // Remove shortcode part (everything before " | ")
  const withoutShortcode = formattedName.includes(" | ")
   ? formattedName.split(" | ")[1]
   : formattedName;
  return withoutShortcode.trim();
 };

 // Apply client-side filtering to existing data
 applyClientSideFilters = () => {
  let filteredAppointments = [...this.state.allAppointments];

  // Apply date filter based on scheduledDate (not originalScheduledDate)
  const reportDateStart = moment(this.state.reportDate).startOf("day");
  const reportDateEnd = moment(this.state.reportDate).endOf("day");

  filteredAppointments = filteredAppointments.filter((appointment) => {
   if (!appointment.appointmentDate) return false;
   const appointmentDate = moment(appointment.appointmentDate);
   return appointmentDate.isSameOrAfter(reportDateStart) && appointmentDate.isSameOrBefore(reportDateEnd);
  });

  console.log("🔍 DEBUG: Date filter applied (scheduledDate):", {
   reportDate: reportDateStart.format("YYYY-MM-DD"),
   beforeDateFilter: this.state.allAppointments.length,
   afterDateFilter: filteredAppointments.length,
  });

  // Apply doctor filter if selected
  if (this.state.selectedDoctor && this.state.selectedDoctorName) {
   const extractedDoctorName = this.extractDoctorNameFromFormatted(
    this.state.selectedDoctorName
   );
   console.log(
    "🔍 DEBUG: Applying client-side doctor filter:",
    extractedDoctorName
   );

   filteredAppointments = filteredAppointments.filter((appointment) => {
    const appointmentDoctorName = appointment.doctorName || "";
    // Try multiple matching strategies
    const matches = [
     // Exact match
     appointmentDoctorName === extractedDoctorName,
     // Match without "DR. " prefix
     appointmentDoctorName.replace(/^DR\.\s*/i, "") ===
      extractedDoctorName.replace(/^DR\.\s*/i, ""),
     // Case-insensitive match
     appointmentDoctorName.toLowerCase() === extractedDoctorName.toLowerCase(),
     // Partial match (contains)
     appointmentDoctorName
      .toLowerCase()
      .includes(extractedDoctorName.toLowerCase()),
    ].some(Boolean);

    return matches;
   });

   console.log("🔍 DEBUG: Client-side filtering result:", {
    original: this.state.allAppointments.length,
    filtered: filteredAppointments.length,
    extractedDoctorName,
   });
  }

  // Apply status filter if selected
  if (this.state.selectedStatus) {
   console.log(
    "🔍 DEBUG: Applying client-side status filter:",
    this.state.selectedStatus
   );
   filteredAppointments = filteredAppointments.filter((appointment) => {
    const appointmentStatus = String(appointment.status || "")
     .trim()
     .toLowerCase();
    return appointmentStatus === this.state.selectedStatus.toLowerCase();
   });
  }

  // Update state with filtered data
  this.setState({ appointments: filteredAppointments });

  // Show feedback to user
  const filterMsg = this.state.selectedDoctorName
   ? `Filtered to ${
      filteredAppointments.length
     } appointments for ${this.extractDoctorNameFromFormatted(
      this.state.selectedDoctorName
     )}`
   : `Showing all ${filteredAppointments.length} appointments`;

  cogoToast.success(filterMsg);
 };

 // Handle branch selection from SingleSelectWrapper
 handleBranchFilterSelect = (branchName) => {
  const selectedBranch = this.state.branches.find(
   (branch) => branch.name === branchName
  );
  this.setState(
   {
    selectedBranch: selectedBranch ? selectedBranch._id : "",
    selectedBranchName: branchName || null,
    selectedDoctor: "", // Reset doctor when branch changes
    selectedDoctorName: null,
   },
   () => {
    // Auto-regenerate report when branch changes
    this.generateReport();
   }
  );
 };

 // Handle doctor selection from SingleSelectWrapper
 handleDoctorFilterSelect = (formattedDoctorName) => {
  // Find the doctor by matching the formatted name
  const selectedDoctor = this.state.doctors.find((doctor) => {
   const formatted = this.getFormattedDoctorName(doctor);
   return formatted === formattedDoctorName;
  });

  this.setState(
   {
    selectedDoctor: selectedDoctor ? selectedDoctor._id : "",
    selectedDoctorName: formattedDoctorName || null,
   },
   () => {
    // Apply filtering immediately if we have data
    if (this.state.allAppointments.length > 0) {
     this.applyClientSideFilters();
    }
   }
  );
 };

 // Handle status selection
 handleStatusFilterSelect = (status) => {
  this.setState(
   {
    selectedStatus: status || "",
   },
   () => {
    // Apply filtering immediately if we have data
    if (this.state.allAppointments.length > 0) {
     this.applyClientSideFilters();
    }
   }
  );
 };

 exportToPDF = () => {
  try {
   const branchName =
    this.state.branches.find((b) => b._id === this.state.selectedBranch)
     ?.name || "All Branches";
   const doctorName = this.state.selectedDoctorName
    ? this.extractDoctorNameFromFormatted(this.state.selectedDoctorName)
    : null;

   // Create a new window for printing
   const printWindow = window.open("", "_blank");

   if (!printWindow) {
    cogoToast.error("Popup blocked. Allow popups to export PDF");
    return;
   }

   // Get data for detailed view - use normalizeGender for consistency
   const males = (this.state.appointments || []).filter(
    (a) => this.normalizeGender(a.patientGender) === "male"
   );
   const females = (this.state.appointments || []).filter(
    (a) => this.normalizeGender(a.patientGender) === "female"
   );

   console.log(
    "🔍 DEBUG: PDF Export - Total appointments:",
    this.state.appointments.length
   );
   console.log(
    "🔍 DEBUG: PDF Export - Males:",
    males.length,
    "Females:",
    females.length
   );

   // Generate summary table HTML for PDF
   const { rows, totals } = this.computeSummary(this.state.appointments);
   const summaryTableHTML = `
      <div class="section">
        <h2>Age/Gender Distribution Summary</h2>
        <table>
          <thead>
            <tr>
              <th>Age Range (Years)</th>
              <th>Male</th>
              <th>Female</th>
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            ${rows
             .map(
              (r) => `
              <tr>
                <td>${r.label}</td>
                <td style="text-align: center;">${r.male}</td>
                <td style="text-align: center;">${r.female}</td>
                <td style="text-align: center;"><strong>${r.total}</strong></td>
              </tr>
            `
             )
             .join("")}
            <tr style="background-color: #f0f0f0; font-weight: bold;">
              <td>TOTAL</td>
              <td style="text-align: center;">${totals.male}</td>
              <td style="text-align: center;">${totals.female}</td>
              <td style="text-align: center;">${totals.total}</td>
            </tr>
          </tbody>
        </table>
      </div>
    `;

   // Generate HTML content for PDF
   const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>IPD Age/Gender Report</title>
          <style>
            /* Ensure landscape orientation when printing */
            @page {
              size: A4 landscape;
              margin: 12mm;
            }
            body { 
              font-family: Arial, sans-serif; 
              margin: 20px; 
              font-size: 12px;
              color: #000;
            }
            .header {
              text-align: center;
              margin-bottom: 30px;
              border-bottom: 2px solid #000;
              padding-bottom: 20px;
            }
            .header h1 {
              margin: 0;
              font-size: 18px;
              font-weight: bold;
            }
            .header p {
              margin: 5px 0;
              font-size: 14px;
            }
            .section {
              margin-bottom: 30px;
            }
            .section h2 {
              font-size: 16px;
              margin-bottom: 15px;
              color: #333;
              border-bottom: 1px solid #ccc;
              padding-bottom: 5px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 10px;
              font-size: 10px;
            }
            th, td {
              border: 1px solid #000;
              text-align: left;
              vertical-align: top;
            }
            th { 
              padding: 12px 8px;
              text-align: center;
              background-color: #f0f0f0;
              font-weight: bold;
            }
            td { 
              padding: 8px;
              line-height: 1.4; 
            }
            tbody tr { 
              page-break-inside: avoid; 
            }
            .no-break {
              page-break-inside: avoid;
            }
            .eye-tag {
              background: #137CBD20;
              color: #137CBD;
              padding: 2px 8px;
              border-radius: 12px;
              font-size: 11px;
              font-weight: 500;
            }
            .diet-tag {
              padding: 2px 8px;
              border-radius: 12px;
              font-size: 11px;
              font-weight: 500;
            }
            .diabetic {
              background: #D9822B20;
              color: #D9822B;
            }
            .non-diabetic {
              background: #0F996020;
              color: #0F9960;
            }
            @media print {
              @page { size: A4 landscape; }
              body { margin: 10px; }
              .header { page-break-after: avoid; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>IPD APPOINTMENT - AGE/SEX SUMMARY</h1>
            <p>${branchName}${doctorName ? ` • ${doctorName}` : ""} - ${moment(
    this.state.reportDate
   ).format("DD-MMM-YYYY")}</p>
            <p>Generated At: ${moment().format("DD/MM/YYYY, HH:mm")}</p>
            <p>Total Appointments: ${this.state.appointments.length} (Male: ${
    males.length
   }, Female: ${females.length})</p>
          </div>
          
          ${summaryTableHTML}
          
          ${
           this.state.showDetailed
            ? `
            <div class="section">
              <h2>Male Patients (${males.length})</h2>
              ${this.generateDetailedTableHTML(males)}
            </div>
            
            <div class="section">
              <h2>Female Patients (${females.length})</h2>
              ${this.generateDetailedTableHTML(females)}
            </div>
          `
            : ""
          }
        </body>
        </html>
      `;

   printWindow.document.write(htmlContent);
   printWindow.document.close();

   // Wait for content to load then trigger print (use setTimeout for better reliability)
   setTimeout(() => {
    printWindow.focus();
    printWindow.print();
   }, 250);

   cogoToast.success("PDF print dialog opened");
  } catch (err) {
   console.error("Error exporting PDF:", err);
   cogoToast.error("Failed to export PDF");
  }
 };

 generateDetailedTableHTML = (appointments) => {
  if (!appointments || appointments.length === 0) {
   return '<p style="text-align: center; color: #666;">No appointments found</p>';
  }

  const headers = [
   "Doctor/Surgeon",
   "Surgery ID",
   "PID/Name of Patient",
   "Age",
   "Sex",
   "Eye",
   "Diet",
   "IOL/Implant",
   "Status",
   "Special Note",
   "Surgery",
   "Remarks",
  ];

  const headerRow = `<tr>${headers.map((h) => `<th>${h}</th>`).join("")}</tr>`;

  const dataRows = appointments
   .map(
    (appointment) => `
      <tr class="no-break">
        <td>${
         appointment.doctorName
          ? appointment.doctorName.replace("DR. ", "")
          : "-"
        }</td>
        <td>${
         appointment.docNo ||
         appointment.bookingId ||
         appointment.surgeryId ||
         "-"
        }</td>
        <td>
          <strong>${appointment.patientId || appointment.pid || "-"}/${
     appointment.patientName || "-"
    }</strong>
          <br/>
          <small style="color: #666;">${
           appointment.appointmentDate
            ? moment(appointment.appointmentDate).format("DD/MM/YYYY HH:mm")
            : "-"
          }</small>
        </td>
        <td style="text-align: center;">${appointment.patientAge || "-"}</td>
        <td style="text-align: center;">${appointment.patientGender || "-"}</td>
        <td style="text-align: center;">
          <span class="eye-tag">${appointment.eye || "-"}</span>
        </td>
        <td style="text-align: center;">
          <span class="diet-tag ${
           appointment.diet === "DIABETIC" ? "diabetic" : "non-diabetic"
          }">${appointment.diet || "-"}</span>
        </td>
        <td>${appointment.lens || "NO LENS"}</td>
        <td style="text-align: center; text-transform: capitalize;">${
         appointment.status || "confirmed"
        }</td>
        <td style="color: #666;">${appointment.specialNote || "-"}</td>
        <td style="line-height: 1.4; word-wrap: break-word;">${
         appointment.surgeryName || appointment.surgery || "-"
        }</td>
        <td style="color: #666; word-wrap: break-word;">${
         appointment.remarks || "-"
        }</td>
      </tr>
    `
   )
   .join("");

  return `
      <table>
        <thead>${headerRow}</thead>
        <tbody>${dataRows}</tbody>
      </table>
    `;
 };

 // Summary helpers
 getAgeBuckets = () => {
  const buckets = [];
  for (let start = 1; start <= 91; start += 10) {
   const end = start + 9;
   buckets.push({ label: `${start}-${end}`, min: start, max: end });
  }
  buckets.push({ label: "100+", min: 100, max: Infinity });
  return buckets;
 };

 computeSummary = (rows) => {
  const buckets = this.getAgeBuckets();
  const summary = buckets.map((b) => ({
   label: b.label,
   male: 0,
   female: 0,
   total: 0,
  }));
  (rows || []).forEach((r) => {
   const age = Number(r.patientAge || 0);
   const gender = this.normalizeGender(r.patientGender);
   const idx = buckets.findIndex((b) => age >= b.min && age <= b.max);
   if (idx >= 0) {
    if (gender === "male") summary[idx].male += 1;
    else if (gender === "female") summary[idx].female += 1;
    else summary[idx].total += 1; // unknown gender
   }
  });
  summary.forEach((s) => (s.total += s.male + s.female));
  const totals = summary.reduce(
   (acc, s) => ({
    male: acc.male + s.male,
    female: acc.female + s.female,
    total: acc.total + s.total,
   }),
   { male: 0, female: 0, total: 0 }
  );
  return { rows: summary, totals };
 };

 renderSummaryTable = () => {
  const { rows, totals } = this.computeSummary(this.state.appointments);
  if (!this.state.appointments.length) {
   return (
    <NonIdealState
     icon="panel-table"
     title="No Data"
     description="No appointments found for the selected criteria"
    />
   );
  }
  return (
   <div
    style={{
     overflowX: "auto",
     border: "1px solid var(--border-color)",
     borderRadius: "8px",
    }}
   >
    <table
     style={{
      width: "100%",
      borderCollapse: "collapse",
      backgroundColor: "var(--bg-surface)",
      fontSize: "14px",
     }}
    >
     <thead>
      <tr
       style={{
        backgroundColor: "var(--bg-secondary)",
        borderBottom: "2px solid var(--border-color)",
       }}
      >
       {["Age Range (Years)", "Male", "Female", "Total"].map((h) => (
        <th
         key={h}
         style={{
          padding: "12px",
          border: "1px solid var(--border-color)",
          textAlign: "left",
          color: "var(--text-primary)",
          fontWeight: 600,
         }}
        >
         {h}
        </th>
       ))}
      </tr>
     </thead>
     <tbody>
      {rows.map((r) => (
       <tr
        key={r.label}
        style={{ borderBottom: "1px solid var(--border-color)" }}
       >
        <td
         style={{
          padding: "10px",
          border: "1px solid var(--border-color)",
          color: "var(--text-primary)",
         }}
        >
         {r.label}
        </td>
        <td
         style={{
          padding: "10px",
          border: "1px solid var(--border-color)",
          color: "var(--text-primary)",
         }}
        >
         {r.male}
        </td>
        <td
         style={{
          padding: "10px",
          border: "1px solid var(--border-color)",
          color: "var(--text-primary)",
         }}
        >
         {r.female}
        </td>
        <td
         style={{
          padding: "10px",
          border: "1px solid var(--border-color)",
          color: "var(--text-primary)",
         }}
        >
         {r.total}
        </td>
       </tr>
      ))}
      <tr
       style={{
        backgroundColor: "var(--bg-secondary)",
        borderTop: "2px solid var(--border-color)",
       }}
      >
       <td
        style={{
         padding: "10px",
         border: "1px solid var(--border-color)",
         fontWeight: 700,
         color: "var(--text-primary)",
        }}
       >
        TOTAL
       </td>
       <td
        style={{
         padding: "10px",
         border: "1px solid var(--border-color)",
         fontWeight: 700,
         color: "var(--text-primary)",
        }}
       >
        {totals.male}
       </td>
       <td
        style={{
         padding: "10px",
         border: "1px solid var(--border-color)",
         fontWeight: 700,
         color: "var(--text-primary)",
        }}
       >
        {totals.female}
       </td>
       <td
        style={{
         padding: "10px",
         border: "1px solid var(--border-color)",
         fontWeight: 700,
         color: "var(--text-primary)",
        }}
       >
        {totals.total}
       </td>
      </tr>
     </tbody>
    </table>
   </div>
  );
 };

 renderDetailedTable = (rows) => {
  if (!rows.length) return null;
  return (
   <div
    style={{
     overflowX: "auto",
     border: "1px solid var(--border-color)",
     borderRadius: "8px",
    }}
   >
    <table
     style={{
      width: "100%",
      borderCollapse: "collapse",
      backgroundColor: "var(--bg-surface)",
      fontSize: "14px",
     }}
    >
     <thead>
      <tr
       style={{
        backgroundColor: "var(--bg-secondary)",
        borderBottom: "2px solid var(--border-color)",
       }}
      >
       {[
        "Doctor/Surgeon",
        "Surgery ID",
        "PID/Name of Patient",
        "Age",
        "Sex",
        "Eye",
        "Diet",
        "IOL/Implant",
        "Status",
        "Special Note",
        "Surgery",
        "Remarks",
       ].map((h) => (
        <th
         key={h}
         style={{
          padding: "12px",
          border: "1px solid var(--border-color)",
          textAlign: "left",
          color: "var(--text-primary)",
          fontWeight: 600,
         }}
        >
         {h}
        </th>
       ))}
      </tr>
     </thead>
     <tbody>
      {rows.map((appointment, idx) => (
       <tr
        key={appointment._id || idx}
        style={{ borderBottom: "1px solid var(--border-color)" }}
       >
        <td
         style={{
          padding: "12px",
          border: "1px solid var(--border-color)",
          fontWeight: 600,
          color: "var(--text-primary)",
         }}
        >
         {appointment.doctorName
          ? appointment.doctorName.replace("DR. ", "")
          : "-"}
        </td>
        <td
         style={{
          padding: "12px",
          border: "1px solid var(--border-color)",
          color: "var(--text-primary)",
         }}
        >
         <span
          onClick={() => this.navigateToDetails(appointment)}
          style={{
           color: "#137cbd",
           cursor: "pointer",
           textDecoration: "underline",
           fontWeight: "600",
          }}
         >
          {appointment.docNo ||
           appointment.bookingId ||
           appointment.surgeryId ||
           "-"}
         </span>
        </td>
        <td
         style={{ padding: "12px", border: "1px solid var(--border-color)" }}
        >
         <div>
          <div
           style={{
            fontWeight: 600,
            color: "var(--text-primary)",
            marginBottom: "4px",
           }}
          >
           {appointment.patientId || appointment.pid || "-"}/
           {appointment.patientName || "-"}
          </div>
          <div
           style={{
            fontSize: "12px",
            color: "var(--text-secondary)",
            display: "flex",
            alignItems: "center",
            gap: "5px",
           }}
          >
           <Icon icon="calendar" size={12} />
           {appointment.appointmentDate
            ? moment(appointment.appointmentDate).format("DD/MM/YYYY HH:mm")
            : "-"}
          </div>
         </div>
        </td>
        <td
         style={{
          padding: "12px",
          border: "1px solid var(--border-color)",
          textAlign: "center",
         }}
        >
         {appointment.patientAge || "-"}
        </td>
        <td
         style={{
          padding: "12px",
          border: "1px solid var(--border-color)",
          textAlign: "center",
         }}
        >
         {appointment.patientGender || "-"}
        </td>
        <td
         style={{
          padding: "12px",
          border: "1px solid var(--border-color)",
          textAlign: "center",
         }}
        >
         <Tag
          minimal
          intent={appointment.eye === "RE" ? "primary" : "success"}
          style={{ fontSize: "12px" }}
         >
          {appointment.eye || "-"}
         </Tag>
        </td>
        <td
         style={{
          padding: "12px",
          border: "1px solid var(--border-color)",
          textAlign: "center",
         }}
        >
         <Tag
          minimal
          intent={appointment.diet === "DIABETIC" ? "warning" : "success"}
          style={{ fontSize: "12px" }}
         >
          {appointment.diet || "-"}
         </Tag>
        </td>
        <td
         style={{
          padding: "12px",
          border: "1px solid var(--border-color)",
          fontStyle: appointment.lens === "NO LENS" ? "italic" : "normal",
          color:
           appointment.lens === "NO LENS"
            ? "var(--text-secondary)"
            : "var(--text-primary)",
         }}
        >
         {appointment.lens || "NO LENS"}
        </td>
        <td
         style={{
          padding: "12px",
          border: "1px solid var(--border-color)",
          textAlign: "center",
         }}
        >
         <Tag
          minimal
          intent={
           appointment.status === "confirmed"
            ? "success"
            : appointment.status === "postponed"
            ? "warning"
            : appointment.status === "discharged"
            ? "primary"
            : appointment.status === "cancelled"
            ? "danger"
            : "none"
          }
          style={{ fontSize: "12px", textTransform: "capitalize" }}
         >
          {appointment.status || "confirmed"}
         </Tag>
        </td>
        <td
         style={{
          padding: "12px",
          border: "1px solid var(--border-color)",
          color: "var(--text-secondary)",
         }}
        >
         {appointment.specialNote || "-"}
        </td>
        <td
         style={{
          padding: "12px",
          border: "1px solid var(--border-color)",
          lineHeight: "1.4",
         }}
        >
         <div
          style={{
           maxWidth: "300px",
           wordWrap: "break-word",
           color: "var(--text-primary)",
           whiteSpace: "pre-line", // This allows \n to create new lines
          }}
         >
          {appointment.surgeryName || appointment.surgery || "-"}
         </div>
        </td>
        <td
         style={{
          padding: "12px",
          border: "1px solid var(--border-color)",
          lineHeight: "1.4",
          color: "var(--text-secondary)",
         }}
        >
         <div style={{ maxWidth: "200px", wordWrap: "break-word" }}>
          {appointment.remarks || "-"}
         </div>
        </td>
       </tr>
      ))}
     </tbody>
    </table>
   </div>
  );
 };

 renderFilters = () => {
  return (
   <Card
    elevation={2}
    style={{
     padding: "20px",
     backgroundColor: "var(--bg-surface)",
     border: "1px solid var(--border-color)",
     marginBottom: "20px",
    }}
   >
    <div
     style={{ display: "flex", alignItems: "center", marginBottom: "15px" }}
    >
     <Icon
      icon="filter"
      style={{ marginRight: "10px", color: "var(--text-primary)" }}
     />
     <H3 style={{ color: "var(--text-primary)", margin: 0 }}>Filters</H3>
     <div
      style={{
       marginLeft: "auto",
       display: "flex",
       alignItems: "center",
       gap: "10px",
      }}
     >
      <Tag intent="success" large>
       {this.state.appointments.length} Appointments
      </Tag>
     </div>
    </div>

    <div
     style={{
      display: "flex",
      alignItems: "center",
      gap: "15px",
      flexWrap: "nowrap",
     }}
    >
     <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
      <Tag minimal style={{ fontSize: "12px", whiteSpace: "nowrap" }}>
       Date
      </Tag>
      <DateInput
       value={this.state.reportDate}
       onChange={(d) => this.handleDateChange(d)}
       formatDate={(date) => moment(date).format("DD-MMM-YYYY")}
       parseDate={(str) => moment(str, "DD-MMM-YYYY").toDate()}
       placeholder="Date"
       style={{ width: "140px" }}
       rightElement={<Icon icon="calendar" size={14} />}
      />
     </div>

     <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
      <Tag minimal style={{ fontSize: "12px", whiteSpace: "nowrap" }}>
       Branch
      </Tag>
      <div style={{ width: "180px" }}>
       <SingleSelectWrapper
        items={[
         `All Branches (${this.state.branches.length})`,
         ...this.createBranchSelectItems(),
        ]}
        selectedItem={
         this.state.selectedBranchName ||
         `All Branches (${this.state.branches.length})`
        }
        placeholder="Search branches..."
        onChange={(value) => {
         if (value === `All Branches (${this.state.branches.length})`) {
          this.handleBranchFilterSelect(null);
         } else {
          this.handleBranchFilterSelect(value);
         }
        }}
        width="100%"
       />
      </div>
     </div>

     <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
      <Tag minimal style={{ fontSize: "12px", whiteSpace: "nowrap" }}>
       Doctor
      </Tag>
      <div style={{ width: "200px" }}>
       <SingleSelectWrapper
        items={[
         `All Doctors (${this.state.doctors.length})`,
         ...this.createDoctorSelectItems(),
        ]}
        selectedItem={
         this.state.selectedDoctorName ||
         `All Doctors (${this.state.doctors.length})`
        }
        placeholder="Search doctors..."
        onChange={(value) => {
         if (value === `All Doctors (${this.state.doctors.length})`) {
          this.handleDoctorFilterSelect(null);
         } else {
          this.handleDoctorFilterSelect(value);
         }
        }}
        width="100%"
       />
      </div>
     </div>

     <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
      <Tag minimal style={{ fontSize: "12px", whiteSpace: "nowrap" }}>
       Status
      </Tag>
      <div style={{ width: "150px" }}>
       <SingleSelectWrapper
        items={[
         "All Statuses",
         "Confirmed",
         "Postponed",
         "Cancelled",
         "Discharged",
        ]}
        selectedItem={
         this.state.selectedStatus
          ? this.state.selectedStatus.charAt(0).toUpperCase() +
            this.state.selectedStatus.slice(1)
          : "All Statuses"
        }
        placeholder="Select status..."
        onChange={(value) => {
         if (value === "All Statuses") {
          this.handleStatusFilterSelect(null);
         } else {
          this.handleStatusFilterSelect(value);
         }
        }}
        width="100%"
       />
      </div>
     </div>

     <div
      style={{
       display: "flex",
       alignItems: "center",
       gap: "12px",
       marginLeft: "auto",
      }}
     >
      <Switch
       label="Detailed View"
       checked={this.state.showDetailed}
       onChange={(e) => this.setState({ showDetailed: e.target.checked })}
      />
      <Button
       intent="primary"
       icon="search"
       onClick={this.generateReport}
       loading={this.state.isGenerating}
      >
       Generate
      </Button>
      <Button
       icon="document"
       onClick={this.exportToPDF}
       disabled={this.state.appointments.length === 0}
       title="Export to PDF"
      >
       PDF
      </Button>
     </div>
    </div>
   </Card>
  );
 };

 renderHeader = () => {
  const branchName =
   this.state.branches.find((b) => b._id === this.state.selectedBranch)?.name ||
   "All Branches";
  const doctorName = this.state.selectedDoctorName
   ? this.extractDoctorNameFromFormatted(this.state.selectedDoctorName)
   : null;
  return (
   <div
    style={{
     textAlign: "center",
     marginBottom: "20px",
     borderBottom: "2px solid var(--border-color)",
     paddingBottom: "12px",
    }}
   >
    <H1 style={{ color: "var(--text-primary)", margin: 0, fontSize: "24px" }}>
     IPD APPOINTMENT - AGE/SEX SUMMARY
    </H1>
    <Text
     style={{
      color: "var(--text-secondary)",
      margin: "5px 0",
      fontSize: "16px",
     }}
    >
     {branchName}
     {doctorName ? ` • ${doctorName}` : ""} -{" "}
     {moment(this.state.reportDate).format("DD-MMM-YYYY")}
    </Text>
   </div>
  );
 };

 renderDetailed = () => {
  const males = (this.state.appointments || []).filter(
   (a) => this.normalizeGender(a.patientGender) === "male"
  );
  const females = (this.state.appointments || []).filter(
   (a) => this.normalizeGender(a.patientGender) === "female"
  );
  return (
   <div style={{ display: "grid", gap: "20px" }}>
    <div>
     <H3 style={{ color: "var(--text-primary)", marginBottom: "10px" }}>
      Male Patients
     </H3>
     {this.renderDetailedTable(males)}
    </div>
    <div>
     <H3 style={{ color: "var(--text-primary)", marginBottom: "10px" }}>
      Female Patients
     </H3>
     {this.renderDetailedTable(females)}
    </div>
   </div>
  );
 };

 render() {
  if (this.state.isLoading) {
   return (
    <NonIdealState
     icon={<Spinner />}
     title="Loading..."
     description="Preparing filters and data"
    />
   );
  }
  return (
   <div
    style={{
     padding: "20px",
     backgroundColor: "var(--bg-primary)",
     color: "var(--text-primary)",
     minHeight: "100vh",
    }}
   >
    <div style={{ marginBottom: "20px" }}>
     <div style={{ display: "flex", alignItems: "center" }}>
      <Icon
       icon="panel-table"
       style={{ marginRight: "12px", color: "var(--intent-primary)" }}
      />
      <H1 style={{ color: "var(--text-primary)", margin: 0 }}>
       IPD Age/Gender
      </H1>
     </div>
     <Text
      style={{
       color: "var(--text-secondary)",
       margin: "5px 0",
       fontSize: "16px",
      }}
     >
      Age-wise and sex-wise distribution of IPD appointments for OT planning
     </Text>
    </div>

    {this.renderFilters()}

    <Card
     elevation={2}
     style={{
      padding: "20px",
      backgroundColor: "var(--bg-surface)",
      border: "1px solid var(--border-color)",
     }}
    >
     {this.renderHeader()}
     {this.state.isGenerating ? (
      <NonIdealState
       icon={<Spinner />}
       title="Generating Report..."
       description="Please wait"
      />
     ) : (
      <div style={{ display: "grid", gap: "20px" }}>
       {this.renderSummaryTable()}
       {this.state.showDetailed && this.renderDetailed()}
      </div>
     )}
    </Card>
   </div>
  );
 }
}

export default inject("authStore")(observer(withRouter(IPDAgeGender)));
